// components/ToastProvider.tsx
'use client';

import * as RXToast from '@radix-ui/react-toast';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
} from 'react';
import { Toast } from './toast';
import styles from './toast-provider.module.css';
import { Icons } from '../symbols';

type ToastVariant = 'info' | 'success' | 'error';

export type ToastMessage = {
  title: string;
  message?: string;
  variant?: ToastVariant;
  type?: 'foreground' | 'background';
  icon?: Icons;
  duration?: number;
};

type ToastProviderProps = PropsWithChildren<{
  duration?: number;
  swipeDirection?: 'up' | 'down' | 'left' | 'right';
}>;

type ToastContextType = {
  publish: (toast: ToastMessage) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({
  children,
  duration: defaultDuration = 5000,
  swipeDirection = 'right',
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<(ToastMessage & { id: string })[]>([]);

  const publish = useCallback((toast: ToastMessage) => {
    setToasts((prev) => [
      ...prev,
      { ...toast, id: crypto.randomUUID(), variant: toast.variant ?? 'info' },
    ]);
  }, []);

  const remove = (id: string) => () => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ publish }}>
      <RXToast.Provider
        swipeDirection={swipeDirection}
        duration={defaultDuration}
      >
        {children}

        {toasts.map(({ id, ...toast }) => (
          <Toast key={id} {...toast} remove={remove(id)} dismissable open />
        ))}

        <RXToast.Viewport className={styles.viewport} />
      </RXToast.Provider>
    </ToastContext.Provider>
  );
}
